// Pure Minesweeper rules. A board is { rows, cols, cells }, where each cell is
// { mine, adjacent, state: "hidden" | "revealed" | "flagged" }. Updates return new boards.

export const createBoard = (rows, cols) => ({
  rows,
  cols,
  cells: Array.from({ length: rows * cols }, () => ({ mine: false, adjacent: 0, state: "hidden" })),
});

export const neighbors = ({ rows, cols }, index) => {
  const r = Math.floor(index / cols);
  const c = index % cols;
  const result = [];
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      const nr = r + dr;
      const nc = c + dc;
      if ((dr || dc) && nr >= 0 && nr < rows && nc >= 0 && nc < cols) {
        result.push(nr * cols + nc);
      }
    }
  }
  return result;
};

// Mines are placed on the first click, never on or around it, so every game opens with a clearing.
export const placeMines = (board, count, safeIndex, random = Math.random) => {
  const excluded = new Set([safeIndex, ...neighbors(board, safeIndex)]);
  const candidates = board.cells.map((_, i) => i).filter((i) => !excluded.has(i));
  if (count > candidates.length) {
    throw new Error(`Cannot place ${count} mines in ${candidates.length} free cells`);
  }

  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
  }
  const mines = new Set(candidates.slice(0, count));

  const cells = board.cells.map((cell, i) => ({ ...cell, mine: mines.has(i) }));
  cells.forEach((cell, i) => {
    cell.adjacent = neighbors(board, i).filter((n) => cells[n].mine).length;
  });
  return { ...board, cells };
};

// Reveals a cell, flooding outward through cells with no adjacent mines.
export const reveal = (board, index) => {
  if (board.cells[index].state !== "hidden") return board;

  const cells = board.cells.slice();
  const queue = [index];
  while (queue.length) {
    const i = queue.pop();
    if (cells[i].state !== "hidden") continue;
    cells[i] = { ...cells[i], state: "revealed" };
    if (!cells[i].mine && cells[i].adjacent === 0) {
      queue.push(...neighbors(board, i));
    }
  }
  return { ...board, cells };
};

export const toggleFlag = (board, index) => {
  const cell = board.cells[index];
  if (cell.state === "revealed") return board;

  const cells = board.cells.slice();
  cells[index] = { ...cell, state: cell.state === "flagged" ? "hidden" : "flagged" };
  return { ...board, cells };
};

// Clicking a revealed number whose flags are all placed reveals the rest of its neighbors.
export const chord = (board, index) => {
  const cell = board.cells[index];
  if (cell.state !== "revealed" || cell.adjacent === 0) return board;

  const around = neighbors(board, index);
  const flags = around.filter((n) => board.cells[n].state === "flagged").length;
  if (flags !== cell.adjacent) return board;

  return around.reduce((b, n) => reveal(b, n), board);
};

export const isLost = (board) => board.cells.some((c) => c.mine && c.state === "revealed");

export const isWon = (board) => board.cells.every((c) => c.mine || c.state === "revealed");
